/**
 * Admin V3 — Financial / Withdrawals — top-level page
 *
 * Mounted at: /admin-v3/financial/withdrawals (see AdminV3.js)
 *
 * GOVERNANCE — STRICT FINANCIAL-CORE READ-ONLY:
 *   - Canonical source of truth: driver_withdrawals collection.
 *   - This module is a visibility surface only. Status transitions occur
 *     via the automated payout engine; this UI does not control execution.
 *   - Manual refresh only (no polling, no WebSocket, no EventSource).
 *
 * AUTHORIZED ENDPOINTS:
 *   - GET /admin/economy/withdrawals (list, server-side status filter)
 *   - GET /admin/economy/withdrawals/summary (4-status aggregate)
 *
 * Defensive UX:
 *   - All 6 WithdrawalStatus values render correctly, including the
 *     historically-drifted `reversed` (per B-2 enum promotion).
 *   - Truncated Stripe Transfer ID in main row, full in expanded detail.
 *   - Idempotency key + raw error message in expansion only.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Wallet } from 'lucide-react';
import { SectionCard } from '../../shared/visualTokens';
import useAdminQuery from '../../shared/hooks/useAdminQuery';
import { t, DEFAULT_LOCALE } from '../../i18n';
import FinancialWithdrawalsSearchBar from './FinancialWithdrawalsSearchBar';
import FinancialWithdrawalsSummary from './FinancialWithdrawalsSummary';
import FinancialWithdrawalsTable from './FinancialWithdrawalsTable';

const SEARCH_DEBOUNCE_MS = 300;
const DEFAULT_LIMIT = 50;

const FinancialWithdrawalsPage = () => {
  const locale = DEFAULT_LOCALE;

  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const tid = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(tid);
  }, [search]);

  const queryParams = { limit: DEFAULT_LIMIT };
  if (status) queryParams.status = status;

  // List endpoint — accepts server-side status filter
  const listQuery = useAdminQuery('/admin/economy/withdrawals', queryParams);

  // Summary endpoint — no params, manual refresh only
  const summaryQuery = useAdminQuery('/admin/economy/withdrawals/summary');

  const allWithdrawals = Array.isArray(listQuery.data?.withdrawals) ? listQuery.data.withdrawals : [];

  // Client-side search over loaded page (id, driver, idempotency_key)
  const withdrawals = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return allWithdrawals;
    return allWithdrawals.filter((w) =>
      (w.id || '').toLowerCase().includes(q) ||
      (w.driver_id || '').toLowerCase().includes(q) ||
      (w.driver_name || '').toLowerCase().includes(q) ||
      (w.driver_email || '').toLowerCase().includes(q) ||
      (w.idempotency_key || '').toLowerCase().includes(q)
    );
  }, [allWithdrawals, debouncedSearch]);

  const isFilterActive = Boolean(debouncedSearch) || Boolean(status);
  const isAtBackendCap = allWithdrawals.length === DEFAULT_LIMIT;
  const showSkeleton = listQuery.loading && !listQuery.data;
  const onRetry = () => { listQuery.refetch(); summaryQuery.refetch(); };

  return (
    <SectionCard
      title={t('financial.withdrawals.title', locale)}
      icon={Wallet}
      iconClass="bg-indigo-100 text-indigo-600"
    >
      <div className="space-y-4">
        {/* Read-only governance subtitle (italic, visible at all times) */}
        <p
          className="text-[11px] italic text-slate-400 -mt-2"
          data-testid="adminv3-financial-withdrawals-subtitle"
        >
          {t('financial.withdrawals.subtitle', locale)}
        </p>

        <FinancialWithdrawalsSearchBar
          status={status}
          onStatusChange={setStatus}
          search={search}
          onSearchChange={setSearch}
          resultsCount={withdrawals.length}
          isFilterActive={isFilterActive}
          loading={listQuery.loading}
          locale={locale}
        />

        <FinancialWithdrawalsSummary
          data={summaryQuery.data}
          loading={summaryQuery.loading}
          locale={locale}
        />

        <FinancialWithdrawalsTable
          withdrawals={withdrawals}
          loading={showSkeleton}
          error={listQuery.error || summaryQuery.error}
          isFilterActive={isFilterActive}
          onRetry={onRetry}
          locale={locale}
        />

        {isAtBackendCap && !listQuery.loading && (
          <p
            className="text-[12px] text-slate-400 italic"
            data-testid="adminv3-financial-withdrawals-cap-hint"
          >
            {t('financial.withdrawals.cap.hint', locale).replace('{n}', String(DEFAULT_LIMIT))}
          </p>
        )}
      </div>
    </SectionCard>
  );
};

export default FinancialWithdrawalsPage;
